import React from 'react';
import {
  BasePeriodFilter,
  BasePayToFilter,
  BaseTrafficTypeFilter,
  BaseLabelsFilter,
} from 'scenes/Trackers/components/Filters';

import UserFilter from 'components/Filters/UserFilter';
import {
  FILTER_PERIOD,
  FILTER_PAY_TO,
  FILTER_TRAFFIC_TYPE,
  FILTER_APPLICATIONS,
  FILTER_CLUSTERS,
  FILTER_NETWORKS,
  FILTER_BUSINESS_PARTNERS,
  FILTER_AFFILIATES,
  FILTER_CAMPAIGNS,
  FILTER_MANAGERS,
  FILTER_OWNERS,
  FILTER_PLATFORMS,
  FILTER_GENRES,
  FILTER_COUNTRIES,
  FILTER_REAL_COUNTRIES,
  FILTER_CHANNELS,
  FILTER_CHANNEL_TYPE,
  FILTER_LABELS,
  FILTER_CLIENTS,
  FILTER_REAL_CP_FLOWS,
  FILTER_DEVICE_TYPE,
  FILTER_REGISTRATION_TYPE,
  FILTER_CAMPAIGNS_TYPES,
  FILTER_FRAUD_TYPE,
  FILTER_RE_COUNTER_TYPES,
} from '../../constants';
import {
  loadTrafficTypes,
  loadApplications,
  loadClusters,
  loadNetworks,
  loadBusinessPartners,
  loadAffiliates,
  loadCampaigns,
  loadManagers,
  loadOwners,
  loadPlatforms,
  loadGenres,
  loadCountries,
  loadChannelTypes,
  loadChannels,
  loadLabels,
  loadClients,
  loadDeviceTypes,
  loadRealCPFlows,
  loadRegistrations,
  loadCampaignTypes,
  loadFraudTypes,
  loadReCounters,
} from '../../sources/filters';
import { isGoGameEnv } from 'App/constants';

import { Filter, FilterActions } from '@gogame/filters';
import {
  FILTER_BRIEFS,
  FILTER_INCLUDE_ARCHIVED,
} from '../../../Tracker/constants';

const DEPENDENCIES = {
  [FILTER_APPLICATIONS]: [FILTER_GENRES],
  [FILTER_NETWORKS]: [FILTER_APPLICATIONS],
  [FILTER_CLUSTERS]: [FILTER_NETWORKS],
  [FILTER_BUSINESS_PARTNERS]: [FILTER_APPLICATIONS, FILTER_NETWORKS],
  [FILTER_AFFILIATES]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_CLUSTERS,
    FILTER_CHANNELS,
    FILTER_CHANNEL_TYPE,
    FILTER_BUSINESS_PARTNERS,
  ],
  [FILTER_CAMPAIGNS]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_AFFILIATES,
    FILTER_INCLUDE_ARCHIVED,
  ],
  [FILTER_OWNERS]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_CLUSTERS,
    FILTER_AFFILIATES,
    FILTER_COUNTRIES,
    FILTER_MANAGERS,
    FILTER_CHANNELS,
  ],
  [FILTER_MANAGERS]: [
    FILTER_APPLICATIONS,
    FILTER_NETWORKS,
    FILTER_CLUSTERS,
    FILTER_AFFILIATES,
    FILTER_COUNTRIES,
    FILTER_OWNERS,
    FILTER_CHANNELS,
  ],
  [FILTER_PLATFORMS]: [FILTER_NETWORKS, FILTER_APPLICATIONS],
  [FILTER_CLIENTS]: [FILTER_PLATFORMS],
  [FILTER_CHANNELS]: [
    FILTER_APPLICATIONS,
    FILTER_AFFILIATES,
    FILTER_NETWORKS,
    FILTER_PLATFORMS,
    FILTER_COUNTRIES,
    FILTER_MANAGERS,
    FILTER_OWNERS,
    FILTER_CHANNEL_TYPE,
  ],
  [FILTER_DEVICE_TYPE]: [FILTER_NETWORKS],
  [FILTER_BRIEFS]: [FILTER_APPLICATIONS],
};

const RegistrationPeriodFilter = ({ payToLocked, ...props }) => (
  <BasePeriodFilter
    {...props}
    name={FILTER_PERIOD}
    payToName={FILTER_PAY_TO}
    withPayTo={!payToLocked}
  />
);

const PayToFilter = ({ ...props }) => {
  return (
    <BasePayToFilter
      name={FILTER_PAY_TO}
      placeholder={'Select date'}
      {...props}
    />
  );
};

const TrafficTypeFilter = (props) => (
  <BaseTrafficTypeFilter
    name={FILTER_TRAFFIC_TYPE}
    source={loadTrafficTypes}
    depends={DEPENDENCIES[FILTER_TRAFFIC_TYPE]}
    {...props}
  />
);

const ApplicationsFilter = (props) => (
  <Filter.MultiSelect
    title={'Applications'}
    placeholder={'All'}
    name={FILTER_APPLICATIONS}
    depends={DEPENDENCIES[FILTER_APPLICATIONS]}
    source={loadApplications}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const ClustersFilter = (props) => (
  <Filter.MultiSelect
    title={'Clusters'}
    placeholder={'All'}
    name={FILTER_CLUSTERS}
    depends={DEPENDENCIES[FILTER_CLUSTERS]}
    source={loadClusters}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const NetworksFilter = (props) => (
  <Filter.MultiSelect
    title={'Networks'}
    placeholder={'All'}
    name={FILTER_NETWORKS}
    depends={DEPENDENCIES[FILTER_NETWORKS]}
    source={loadNetworks}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const BusinessPartnersFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Business Partners'}
      placeholder={'All'}
      name={FILTER_BUSINESS_PARTNERS}
      source={loadBusinessPartners}
      depends={DEPENDENCIES[FILTER_BUSINESS_PARTNERS]}
      compareActions={[<FilterActions.Equalize />]}
      {...props}
    />
  );
};
const AffiliatesFilter = (props) => {
  return (
    <Filter.MultiSelect
      title={'Affiliates'}
      placeholder={'All'}
      name={FILTER_AFFILIATES}
      source={loadAffiliates}
      depends={DEPENDENCIES[FILTER_AFFILIATES]}
      compareActions={[<FilterActions.Equalize />]}
      {...props}
    />
  );
};

const CampaignsFilter = (props) => {
  return (
    <Filter.MultiSelect
      serverSideSearch={true}
      title={'Campaigns'}
      placeholder={'All'}
      name={FILTER_CAMPAIGNS}
      source={loadCampaigns}
      depends={DEPENDENCIES[FILTER_CAMPAIGNS]}
      dropdownInputPlaceholder={'Search by name and id'}
      compareActions={[<FilterActions.Equalize />]}
      {...props}
    />
  );
};

const ManagersFilter = (props) => (
  <Filter.MultiSelect
    title={'Managers'}
    placeholder={'All'}
    name={FILTER_MANAGERS}
    source={loadManagers}
    withAll={false}
    depends={DEPENDENCIES[FILTER_MANAGERS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const OwnersFilter = (props) => (
  <UserFilter
    title={'Owners'}
    placeholder={'All'}
    name={FILTER_OWNERS}
    source={loadOwners}
    withAll={false}
    depends={DEPENDENCIES[FILTER_OWNERS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const PlatformsFilter = (props) => (
  <Filter.MultiSelect
    title={'Platforms'}
    placeholder={'All'}
    name={FILTER_PLATFORMS}
    depends={DEPENDENCIES[FILTER_PLATFORMS]}
    source={loadPlatforms}
    withAll={true}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const GenresFilter = (props) => (
  <Filter.MultiSelect
    title={'Genres'}
    placeholder={'All'}
    name={FILTER_GENRES}
    source={loadGenres}
    depends={DEPENDENCIES[FILTER_GENRES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const CountriesFilter = (props) => (
  <Filter.MultiSelect
    title={'Countries'}
    placeholder={'All'}
    name={FILTER_COUNTRIES}
    source={loadCountries}
    depends={DEPENDENCIES[FILTER_COUNTRIES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const RealCountriesFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_REAL_COUNTRIES}
    title={'Real countries'}
    placeholder={'All'}
    source={loadCountries}
    depends={DEPENDENCIES[FILTER_REAL_COUNTRIES]}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const ChannelTypesFilter = (props) => (
  <Filter.MultiSelect
    title={'Channel type'}
    name={FILTER_CHANNEL_TYPE}
    placeholder={'All'}
    source={loadChannelTypes}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const ChannelsFilter = (props) => (
  <Filter.MultiSelect
    title={'Channels'}
    placeholder={'All'}
    name={FILTER_CHANNELS}
    source={loadChannels}
    depends={DEPENDENCIES[FILTER_CHANNELS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const LabelsFilter = (props) => (
  <BaseLabelsFilter
    name={FILTER_LABELS}
    source={loadLabels}
    depends={DEPENDENCIES[FILTER_LABELS]}
    {...props}
  />
);

const ClientsFilter = (props) => (
  <Filter.MultiSelect
    title={'Clients'}
    placeholder={'All'}
    name={FILTER_CLIENTS}
    source={loadClients}
    withAll={false}
    depends={DEPENDENCIES[FILTER_CLIENTS]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const CPFlowFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_REAL_CP_FLOWS}
    title={'CP flow'}
    placeholder={'All'}
    source={loadRealCPFlows}
    withAll={false}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const DeviceTypeFilter = (props) => (
  <Filter.MultiSelect
    name={FILTER_DEVICE_TYPE}
    title={'Real Device type'}
    source={loadDeviceTypes}
    placeholder={'All'}
    depends={DEPENDENCIES[FILTER_DEVICE_TYPE]}
    {...props}
  />
);

const RegistrationsFilter = (props) => (
  <Filter.Select
    name={FILTER_REGISTRATION_TYPE}
    title={'Registrations'}
    source={loadRegistrations}
    {...props}
  />
);

const CampaignTypeFilter = (props) => (
  <Filter.MultiSelect
    title={'Campaign Type'}
    placeholder={'All'}
    name={FILTER_CAMPAIGNS_TYPES}
    source={loadCampaignTypes}
    depends={DEPENDENCIES[FILTER_CAMPAIGNS_TYPES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

const FraudTypeFilter = (props) => (
  <Filter.Select
    name={FILTER_FRAUD_TYPE}
    title={'Fraud'}
    source={loadFraudTypes}
    placeholder={'Select fraud type'}
    {...props}
  />
);

const RECounterFilter = (props) => (
  <Filter.MultiSelect
    title={'RE Counter'}
    placeholder={'All'}
    name={FILTER_RE_COUNTER_TYPES}
    source={loadReCounters}
    depends={DEPENDENCIES[FILTER_RE_COUNTER_TYPES]}
    compareActions={[<FilterActions.Equalize />]}
    {...props}
  />
);

export default {
  RegistrationPeriod: RegistrationPeriodFilter,
  PayTo: PayToFilter,
  TrafficType: isGoGameEnv ? TrafficTypeFilter : null,
  Applications: ApplicationsFilter,
  Networks: NetworksFilter,
  Clusters: ClustersFilter,
  BusinessPartners: BusinessPartnersFilter,
  Affiliates: AffiliatesFilter,
  Campaigns: CampaignsFilter,
  Managers: isGoGameEnv ? ManagersFilter : null,
  Owners: isGoGameEnv ? OwnersFilter : null,
  Platforms: PlatformsFilter,
  Genres: isGoGameEnv ? GenresFilter : null,
  Countries: CountriesFilter,
  RealCountries: RealCountriesFilter,
  ChannelTypes: isGoGameEnv ? ChannelTypesFilter : null,
  Channels: isGoGameEnv ? ChannelsFilter : null,
  Labels: isGoGameEnv ? LabelsFilter : null,
  Clients: isGoGameEnv ? ClientsFilter : null,
  DeviceType: isGoGameEnv ? DeviceTypeFilter : null,
  CPFlow: CPFlowFilter,
  Registrations: RegistrationsFilter,
  CampaignType: CampaignTypeFilter,
  FraudType: FraudTypeFilter,
  RECounter: isGoGameEnv ? RECounterFilter : null,
};
